import { useCallback, useEffect, useRef, useState } from 'react'

import { useOnline } from '../../hooks/useOutbox'

/**
 * The one message line above the input (1.80.0).
 *
 * From use, with a screenshot: grey "Ohne Netz lässt sich der Tag nicht
 * auswerten." and red "Eine Sprachnachricht braucht Netz …" stood on top of
 * each other -- "nur die letzte Meldung da stehen, nicht wie im Bild 2
 * verschiedene" -- and both were still there with the net back: "Fehlermeldungen
 * bleiben stehen, auch wenn wieder Netz da ist".
 *
 * Until here four places each kept a line of their own: the plus menu
 * (`aktionStand`), the microphone (`tonHinweis`), the attachments
 * (`problem`) and sending. Each cleared only itself, and only on its own next
 * action; none of them listened for the net. They keep their state -- that is
 * where the reason lives -- and report it here; **the newest one stands**.
 *
 * The rules, and they are the whole point:
 *
 * - **One line.** A new message replaces the old one, whoever sent it.
 * - **"Only with a net" is red**, wherever it comes from (`ohneNetz`). The
 *   day card said it in grey, the microphone in red.
 * - **It goes when the net comes back.** A sentence about the missing net is
 *   wrong the moment the net is there.
 * - **An error goes with the next choice** (1.117.4, `neueWahl()`). From use:
 *   "sollte diese ohnehin nur solange stehen, bis eine andere Option
 *   ausgewählt wird". Opening a menu -- the plus menu or a bubble's -- is that
 *   choice. A hint about something running stays.
 * - **A finished sentence from the plus menu goes with the next message**
 *   (1.142.0, `nachNachricht()` in `usePlusMenu`). From use: "sie sollten
 *   spätestens bei der nächsten Nachricht weggehen".
 *
 * Since 1.117.4 the line belongs to the chat page and not to the input:
 * the bubble's menu ("Streckenstatistik ändern", "Beitrag davor einfügen",
 * the separator) said "Ohne Netz …" in a line of its own under the timeline,
 * which listened neither for the net nor for the next choice -- the second
 * line this hook was built to end.
 */
export type Meldungsart = 'fehler' | 'hinweis'

export interface Meldung {
  /** Who reported it -- a later "clear" from the same place clears only its own. */
  quelle: string
  text: string
  art: Meldungsart
  /** The sentence is about the missing net and goes when the net returns. */
  ohneNetz: boolean
  /** Counts up, so that the same sentence reported twice is still news. */
  n: number
}

export function useMeldezeile() {
  const [meldung, setMeldung] = useState<Meldung | null>(null)
  const zaehler = useRef(0)

  const melde = useCallback(
    (quelle: string, text: string, art: Meldungsart = 'fehler', ohneNetz = false) => {
      zaehler.current += 1
      setMeldung({ quelle, text, art, ohneNetz, n: zaehler.current })
    },
    [],
  )

  /** Clear what this place reported -- not what another one reported since. */
  const raeume = useCallback((quelle: string) => {
    setMeldung((jetzt) => (jetzt?.quelle === quelle ? null : jetzt))
  }, [])

  /** A new choice: an error from before is no longer the news. */
  const neueWahl = useCallback(() => {
    setMeldung((jetzt) => (jetzt?.art === 'fehler' ? null : jetzt))
  }, [])

  const online = useOnline()
  useEffect(() => {
    if (online) setMeldung((jetzt) => (jetzt?.ohneNetz === true ? null : jetzt))
  }, [online])

  return { meldung, melde, raeume, neueWahl, online }
}

export type Meldezeile = ReturnType<typeof useMeldezeile>
